import { toASCII } from 'punycode';
import { g, Cookie, Sessions } from './';

/**
 * An easy way to loop through objects and run a callback function, similar to jQuery.each.
 *
 * @since 5.0.0
 *
 * @param   {Object}   o  The object to loop through
 * @param   {Function} cb The callback function to run while looping through the object.
 * @returns {void}
 */
export function each(o, cb) {
	if ('object' !== typeof o) {
		throw new Error('First parameter must be an object');
	}

	for (let key in o) {
		if (hasProp(o, key)) {
			cb(key, o[key]);
		}
	}
}

/**
 * Appends the passed element to the document head, or document element if the
 * head tag doesn't exist.
 *
 * @param {Element} element The element to append to the head.
 *
 * @returns {void}
 */
export function appendToHead(element) {
	(document.getElementsByTagName('head')[0] || document.documentElement).appendChild(element);
}

/**
 * Helper function to add a script to the page.
 *
 * @since 5.0.0
 *
 * @param   {string}   src     The valid http(s) source for the JS file to load.
 * @param   {boolean}  async   Whether or not to load this async.
 * @param   {Function} onLoad  The callback function to run when the script has finished loading on the page.
 * @param   {Function} onError The callback function to run when the script has finished with an error.
 * @param   {string}   name    The name of the function if using requirejs
 * @param   {Function} filter  A function to filter the script before it is loaded. This allows attributes to be added.
 * @returns {Promise}          Returns promise which resolves with the script dom element.
 */
export function addScript(src, async = true, onLoad, onError, name, filter) {
	if (isFunction(window.requirejs) && name) {
		let config = {
			paths: {},
		};

		config.paths[name] = src.replace(/\.js$/, '');

		window.requirejs.config(config);

		return new Promise((resolve) => {
			window.requirejs([name], (script) => {
				// Run the callback function only if it's a function. The second parameter tells our function that this was set via requireJs
				if (isFunction(onLoad)) {
					onLoad(script, true);
				}
				resolve(script);
			});
		});
	}

	let script = document.createElement('script');
	script.src = src;
	script.async = async;

	// Let's filter the script to add any necessary attributes as needed.
	if (isFunction(filter)) {
		script = filter(script);
	}

	script.onload = () => {
		if (isFunction(onLoad)) {
			onLoad(script);
		}
	};
	script.onerror = () => {
		if (isFunction(onError)) {
			onError(script);
		}
	};

	// Attempt to append it to the <head>, otherwise append to the document.
	appendToHead(script);
	return Promise.resolve(script);
}

/**
 * Checks whether or not this is a function.
 *
 * @since 5.0.0
 *
 * @param   {Function} f The parameter to check if it is a function or not.
 * @returns {boolean}    Whether or not this is a function.
 */
export function isFunction(f) {
	return 'function' === typeof f;
}

/**
 * Randomly pick a key from the array for our split tests.
 *
 * @since 5.0.0
 *
 * @param   {Array}  array The array that has keys that we need to randomly pick from.
 * @returns {integer}      The key number from the array.
 */
export function randomKey(array) {
	return Math.floor(Math.random() * array.length);
}

/**
 * Shuffle an array in a random order.
 *
 * @since 5.0.0
 *
 * @param   {Array} array The array that has keys that we need to randomly pick from.
 * @returns {Array}       The shuffled array.
 */
export function shuffleArray(array) {
	let currentIndex = array.length;
	let temporaryValue;
	let randomIndex;

	// While there remain elements to shuffle...
	while (0 !== currentIndex) {
		// Pick a remaining element...
		randomIndex = Math.floor(Math.random() * currentIndex);
		currentIndex -= 1;

		// And swap it with the current element.
		temporaryValue = array[currentIndex];
		array[currentIndex] = array[randomIndex];
		array[randomIndex] = temporaryValue;
	}

	return array;
}

/**
 * Returns the current domain with www.
 *
 * @param {string} domain The domain to remove the www from.
 *
 * @returns {string} domain The domain without www.
 */
export const removeWww = (domain) => {
	if (domain.startsWith('www.')) {
		domain = domain.substring(4);
	}

	return domain;
};

/**
 * Get the domain of the current site.
 *
 * @since 5.0.0
 *
 * @returns {string} The domain name as a string.
 */
export function getDomain() {
	return toASCII(removeWww(window.location.hostname));
}

/**
 * Get the tld of the current site.
 *
 * @since 5.3.15
 *
 * @returns {string} The tld as a string.
 */
export function tld() {
	let m = getDomain().match(/[a-z0-9][a-z0-9-]{0,126}[a-z0-9]\.[a-z]{2,}$/i);
	return m ? m[0] : getDomain();
}

/**
 * Get the api domain to use for the current site.
 *
 * @since 5.3.15
 *
 * @param   {Object} d The data/defaults object.
 * @returns {string} The domain name as a string.
 */
export function omApiDomain(d) {
	return inArray(tld(), g.OM_DOMAINS) ? tld() : g[d.wp ? 'WP_DOMAIN' : 'API_DOMAIN'];
}

/**
 * Get the url path of the current page, preceding slash removed,
 * but trailing left intact, and normalized to lowercase.
 *
 * @since 5.3.4
 *
 * @param   {bool}   d Whether or not to decode the URI.
 * @returns {string}   The full url path.
 */
export function urlPathWithTrailing(d = false) {
	return cleanPathWithTrailing(urlPathFull(d));
}

/**
 * Get the url path of the current page, trailing slash removed,
 * but preceding left intact, and normalized to lowercase.
 *
 * @since 5.3.4
 *
 * @param   {bool}   d Whether or not to decode the URI.
 * @returns {string}   The full url path.
 */
export function urlPathWithPreceding(d = false) {
	return cleanPathWithPreceding(urlPathFull(d));
}

/**
 * Get the full url path of the current page.
 *
 * @since 5.0.0
 *
 * @param   {bool}   d Whether or not to decode the URI.
 * @returns {string}   The full url path.
 */
export function urlPathFull(d = false) {
	let p = window.location.pathname;
	return (d ? decodeURIComponent(p) : p).toLowerCase();
}

/**
 * Get the url path of the current page, parsed.
 *
 * @since 5.0.0
 *
 * @param   {bool}   d Whether or not to decode the URI.
 * @returns {string}   The url path.
 */
export function urlPath(d = false) {
	return cleanPath(urlPathFull(d));
}

/**
 * Get the hash attached to the current url.
 *
 * @since 5.0.0
 *
 * @param   {bool}   d Whether or not to decode the URI.
 * @returns {string}   The hash of the url (if it exists)
 */
export function urlHash(d = false) {
	let h = window.location.hash.substr(1);
	return d ? decodeURIComponent(h) : h;
}

/**
 * Retrieves the referrer for the current page.
 *
 * @since 5.0.0
 *
 * @returns {string} The referrer for the current page.
 */
export function referrer() {
	return document.referrer || '';
}

/**
 * Create a query string of all arguments in an object.
 *
 * @since 5.0.0
 *
 * @param   {Object} o The object that will be turned into params.
 * @returns {string}   The parameters joined together via '&'.
 */
export function param(o) {
	return keys(o)
		.map((key) => {
			let val = o[key];
			if ('object' === typeof val && null !== val) {
				val = param(val);
			}
			return encodeURIComponent(key) + '=' + encodeURIComponent(val);
		})
		.join('&');
}

/**
 * Generate a unique ID for each new user.
 *
 * @since 5.0.0
 *
 * @returns {string} The visitor ID as a string.
 */
export function createVisitorId() {
	let str = '';
	let chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
	let length = 96;
	for (let i = length; 0 < i; --i) {
		str += chars[Math.floor(Math.random() * chars.length)];
	}
	return str;
}

/**
 * Retrieves the current query parameters
 *
 * @since 5.0.0
 *
 * @param   {string} str A string to parse.
 * @returns {Object}     An object of query arguments and values.
 */
export function queryArgs(str) {
	return (str || document.location.search || document.location.hash)
		.replace(/(^\?)|(^#)/, '')
		.split('&')
		.map(
			function (n) {
				if ('undefined' !== typeof n) {
					n = n.split('=');
					if (n[0]) {
						this[n[0]] = n[1] || '';
					}
					return this;
				}
			}.bind({})
		)[0];
}

/**
 * Adds parameters to the query string.
 *
 * @since 5.0.0
 *
 * @param   {string} uri The URI that we are adding values to.
 * @param   {string} key The key for the parameter we are adding.
 * @param   {string} val The value for the parameter we are adding.
 * @returns {string}     The URL with any query args amended or added.
 */
export function addQueryArg(uri, key, val) {
	if (!key) {
		throw new Error('A required parameter is missing.');
	}
	var re = new RegExp('([?|&])' + key + '=.*?(&|#|$)', 'i');
	if (uri.match(re)) {
		return uri.replace(re, '$1' + key + '=' + val + '$2');
	} else {
		let hash = '';
		let sep = -1 !== uri.indexOf('?') ? '&' : '?';
		if (-1 !== uri.indexOf('#')) {
			hash = uri.replace(/.*#/, '#');
			uri = uri.replace(/#.*/, '');
		}
		return uri + sep + key + '=' + val + hash;
	}
}

/**
 * Return keys from an object.
 *
 * @since 5.0.0
 *
 * @param   {Object} o The Object to get keys from.
 * @returns {Array}    An array of keys from the object.
 */
export function keys(o) {
	return isObject(o) ? Object.keys(o) : [];
}

/**
 * Return values from an object.
 *
 * @since 5.0.0
 *
 * @param   {Object} o The Object to get values from.
 * @returns {Array}    An array of values from the object.
 */
export function values(o) {
	return isObject(o) ? Object.values(o) : [];
}

/**
 * Check whether the global omhide=true variable is set.
 *
 * @since 5.0.0
 *
 * @returns {boolean} Whether or not the omhide argument is set.
 */
export function globalHideArgs() {
	return queryArgTruthy('omhide');
}

/**
 * Check whether the global omclear=true variable is set.
 *
 * @since 5.0.0
 *
 * @returns {boolean} Whether or not the omclear argument is set.
 */
export function clearOmCookiesStorage() {
	return queryArgTruthy('omclear');
}

/**
 * Check whether the global omdebug=true variable (or window._omdev.data.omdebug) is set.
 *
 * @since 5.1.10
 *
 * @returns {boolean} Whether or not the omdebug argument is set.
 */
export function debugEnabled() {
	if (hasTestFlag('omdebug')) {
		return true;
	}

	if (storageAvailable('sessionStorage') && sessionStorage.getItem('omdebug')) {
		return true;
	}

	return hasProp(window, '_omdev') && window._omdev.data && window._omdev.data.omdebug;
}

/**
 * Check whether the global omdev=true variable (or window._omdev) is set.
 *
 * @since 5.15.13
 *
 * @returns {boolean} Whether or not dev is enabled.
 */
export function devEnabled() {
	if (hasTestFlag('omdev')) {
		return true;
	}

	if (storageAvailable('sessionStorage') && sessionStorage.getItem('omdev')) {
		return true;
	}

	return hasProp(window, '_omdev') && window._omdev;
}

/**
 * Check whether the global omdisabletracking=true variable (or window._omdisabletracking) is set.
 *
 * @since 5.22.6
 *
 * @returns {boolean} Whether or not the omdisabletracking argument is set.
 */
export function disableTrackingEnabled() {
	if (hasTestFlag('omdisabletracking')) {
		return true;
	}
	return hasProp(window, '_omdisabletracking') && window._omdisabletracking;
}

/**
 * Determines if a given value exists in the object.
 *
 * @since 5.0.0
 *
 * @param   {*}       val The value to lookup.
 * @param   {Object}  o   The object to look through.
 * @returns {boolean}     Whether or not the value exists.
 */
export function valueExistsInObject(val, o) {
	return keys(o).some((key) => {
		return o[key] === val;
	});
}

/**
 * Returns an array of keys that exist in both objects.
 *
 * @since 5.0.0
 *
 * @param   {Object}    o1 The first object to check.
 * @param   {Object}    o2 The second object to check.
 * @returns {Array.<*>}    An array of matching keys.
 */
export function matchingKeys(o1, o2) {
	return keys(o1).filter((key) => {
		return -1 !== keys(o2).indexOf(key);
	});
}

/**
 * Turns a string (word) and makes the first letter lowercase. This is similar to `lcfirst` in PHP.
 *
 * @param {string} w The string to make the first letter uppercase.
 *
 * @returns {string} The word with the first letter uppercase.
 */
export function toLowerCaseFirst(w) {
	return w.charAt(0).toLowerCase() + w.slice(1);
}

/**
 * Turns a string (word) and makes the first letter uppercase. This is similar to `ucfirst` in PHP.
 *
 * @param {string} w The string to make the first letter uppercase.
 *
 * @returns {string} The word with the first letter uppercase.
 */
export function toUpperCaseFirst(w) {
	return w.charAt(0).toUpperCase() + w.slice(1);
}

/**
 * Convert a rule type from lowercase with dashes to uppercase without dashes:
 *
 * 'url-path' becomes 'UrlPath'
 *
 * @since 5.0.0
 *
 * @param   {string} name The class name we need to convert.
 * @returns {string}      The converted class name.
 */
export function convertClassName(name) {
	let parts = name.split('-');

	each(parts, (i, v) => {
		parts[i] = toUpperCaseFirst(v);
	});

	return parts.join('');
}

/**
 * Convert a rule type from lowercase with dashes to camel case:
 *
 * 'url-path' becomes 'urlPath'
 *
 * @param {string} name The class name we need to convert.
 *
 * @returns {string} The converted class name.
 */
export function convertFuncName(name) {
	return toLowerCaseFirst(convertClassName(name));
}

/**
 * Convert an operator type from lowercase with dashes to uppercase without dashes:
 *
 * 'url-any' becomes 'urlAny'
 *
 * @since 5.0.0
 *
 * @param   {string} type The operator type we need to convert.
 * @returns {string}      The converted operator type.
 */
export function convertOperatorType(type) {
	if (!type) {
		type = new String('');
		type.op = null;
		type.not = false;

		return type;
	}

	let parts = type.split('-');
	let first = true;

	each(parts, (i, v) => {
		if (first) {
			first = false;
			return;
		}
		parts[i] = toUpperCaseFirst(v);
	});

	type = new String(type);
	type.not = 'not' === parts[0] || 0 === type.indexOf('url-not') || 0 === type.indexOf('not');
	type.op = parts.join('');

	return type;
}

/**
 * Determine if the operator is a regex/notRegex operator.
 *
 * @param {string} operator The operator to check.
 *
 * @returns {boolean} Whether or not the operator is a regex/notRegex operator.
 */
export function operatorIsRegexType(operator) {
	return 'regex' === operator || 'notRegex' === operator;
}

/**
 * Check if a value exists in an array.
 *
 * @since 5.0.0
 *
 * @param   {string|number} a The string to check against in the array.
 * @param   {Array}         b The array to check against.
 * @returns {boolean}         Whether or not the item is in the array.
 */
export function inArray(a, b) {
	return -1 < b.indexOf(a) || false;
}

/**
 * Checking if an HTML element has a class.
 *
 * @since 5.0.0
 *
 * @param   {Element} el   The element we are checking.
 * @param   {string}  name The class name we are checking against.
 * @returns {boolean}      Whether or not the element has the class.
 */
export function hasClass(el, name) {
	if (Array.isArray(name)) {
		return !!name.find((n) => hasClass(el, n));
	}

	return _el(el).classList ? el.classList.contains(name) : new RegExp('\\b' + name + '\\b').test(el.className);
}

/**
 * Adding a class to an element.
 *
 * @since 5.0.0
 *
 * @param   {Element} el   The element we are adding a class to.
 * @param   {string}  name The class name we are checking against.
 * @returns {void}
 */
export function addClass(el, name = '') {
	name = name.split(' ');
	each(name, (i, n) => {
		try {
			if (_el(el).classList) {
				el.classList.add(n);
			} else if (!hasClass(el, n)) {
				el.className += ' ' + n;
			}
		} catch (e) {}
	});
}

/**
 * Removing a class from an element.
 *
 * @since 5.0.0
 *
 * @param   {Element} el   The element we are removing a class from.
 * @param   {string}  name The class name we are checking against.
 * @returns {void}
 */
export function removeClass(el, name = '') {
	name = name.split(' ');
	each(name, (i, n) => {
		try {
			if (_el(el).classList) {
				el.classList.remove(n);
			} else {
				el.className = el.className.replace(new RegExp('\\b' + n + '\\b', 'g'), '');
			}
		} catch (e) {}
	});
}

/**
 * Returns the current timestamp.
 *
 * @since 5.0.0
 *
 * @returns {number} The time in milliseconds.
 */
export function currentTime() {
	return new Date().getTime();
}

/**
 * Attach an event listener to an element.
 *
 * @since 5.0.0
 *
 * @param   {Element}  el A DOM element to attach an event lister to.
 * @param   {string}   ev A string indicating which event to attach.
 * @param   {Function} cb A callback function for when the listener is fired.
 * @param   {Object}   op An object which contains options. For example: { once: true }
 * @returns {Element}     The original element is returned.
 */
export function on(el, ev = '', cb, op) {
	if (!_el(el)._omns) {
		el._omns = {};
	}
	el._omns[ev] = cb;
	el.addEventListener(ev.split('.')[0], cb, op || false);
	return el;
}

/**
 * Disable an event listener from an element.
 *
 * @since 5.0.0
 *
 * @param   {Element} el A DOM element to attach an event lister to.
 * @param   {string}  ev A string indicating which event to attach.
 * @returns {Element}    The original element is returned.
 */
export function off(el, ev) {
	if (!_el(el)._omns || !el._omns[ev]) {
		return el;
	}
	el.removeEventListener(ev.split('.')[0], el._omns[ev]);
	delete el._omns[ev];
	return el;
}

/**
 * Get the number of pixels to the top of the specified element, or window.
 *
 * If no element is passed, we default to the return of `windowScrollTop()`.
 *
 * @since 5.0.0
 *
 * @param {HTMLElement} userScrollElement The user-specified scroll element to target.
 *
 * @returns {number} The number of pixels to the top of the element or page.
 */
export function scrollTop(userScrollElement) {
	return userScrollElement ? userScrollElement.scrollTop : windowScrollTop();
}

/**
 * Get the number of pixels to the top of the window.
 *
 * @since 5.0.0
 *
 * @returns {number} The number of pixels to the top of the page.
 */
export function windowScrollTop() {
	return window.pageYOffset || document.documentElement.scrollTop;
}

/**
 * The percentage that the user has scrolled down the page.
 *
 * @since 5.0.0
 *
 * @param {HTMLElement} userScrollElement The user-specified scroll element to target.
 *
 * @returns {number} The percentage of scroll down the page.
 */
export function scrollPercent(userScrollElement) {
	const st = 'scrollTop';
	const sh = 'scrollHeight';
	const ch = 'clientHeight';

	// Use the passed scroll element if it's valid.
	if (userScrollElement) {
		// Cache the scroll height of custom scroll element as well. Caching this value is done to help
		// us handle cases where elements in the page change the length of a page and could affect our
		// scroll calculations in a way we think the user has scrolled further than they have.
		// Caching the scroll height of a custom scroll element should be analogous to what we do with the
		// omScrollHeight session storage.
		const userElScrollHeight = userScrollElement.dataset.omScrollHeight || userScrollElement[sh];
		userScrollElement.dataset.omScrollHeight = userElScrollHeight;
		return Math.round((userScrollElement[st] / (userElScrollHeight - userScrollElement[ch])) * 100);
	}

	const documentElement = document.documentElement;
	const documentBody = document.body;
	const scrollTop = windowScrollTop() || documentBody[st];

	// Get the cached scroll height if available
	// https://github.com/awesomemotive/optinmonster-api-js/issues/681
	// Use the maximum of element and body scroll heights for accurate calculation
	const scrollHeight = Sessions.get('omScrollHeight') || Math.max(documentElement[sh], documentBody[sh]);

	// Use the actual viewport height instead of documentElement.clientHeight
	const viewportHeight = window.innerHeight || documentElement[ch] || documentBody[ch];

	// Calculate the actual scrollable distance
	const scrollableDistance = scrollHeight - viewportHeight;

	// Ensure we don't divide by zero or negative numbers
	if (0 >= scrollableDistance) {
		return 0;
	}

	return Math.round((scrollTop / scrollableDistance) * 100);
}

/**
 * Append an element at the end of the children of another element.
 *
 * @since 5.0.0
 *
 * @param   {Element} el The parent element that will append the child.
 * @param   {Element} ch The child element to be appended.
 * @returns {Element}    The child element is returned.
 */
export function append(el, ch) {
	return _el(el).appendChild(_el(ch));
}

/**
 * Prepend an element at the beginning of the children of another element.
 *
 * @since 5.0.0
 *
 * @param   {Element} el The parent element that will prepend the child.
 * @param   {Element} ch The child element to be prepended.
 * @returns {Element}    The child element is returned.
 */
export function prepend(el, ch) {
	return _el(el).insertBefore(_el(ch), el.firstChild);
}

/**
 * Append an element after the current element.
 *
 * @since 5.0.0
 *
 * @param   {Element} el  An element to insert a sibling after.
 * @param   {Element} sib The sibling to add.
 * @returns {void}
 */
export function after(el, sib) {
	return _el(el).parentNode.insertBefore(_el(sib), el.nextSibling);
}

/**
 * Append an element before the current element.
 *
 * @since 5.0.0
 *
 * @param   {Element} el  An element to insert a sibling before.
 * @param   {Element} sib The sibling to add.
 * @returns {void}
 */
export function before(el, sib) {
	return _el(el).parentNode.insertBefore(_el(sib), el);
}

/**
 * Tries to get the next sibling element. If an optional filter is passed, it will find the next
 * element of that type. For example: 'a' or 'div'.
 *
 * @since 5.0.0
 *
 * @param   {Element}      el The element we are currently on.
 * @param   {string}       f  An optional filter for finding specific types of elements.
 * @returns {Element|null}    The next element or null.
 */
export function next(el, f) {
	return nextAll(_el(el), f, true);
}

/**
 * Tries to get the ALL next sibling elements. If an optional filter is passed, it will find the next
 * elements of only that type. For example: 'a' or 'div', or '.pizza'.
 *
 * @since 5.0.0
 *
 * @param   {Element}            el     The element we are currently on.
 * @param   {string}             filter An optional filter for finding specific types of elements.
 * @param   {boolean}            first  Whether to return first or all elements.
 * @returns {Element|Array|null}        Returns the element, or an array of elements or null.
 */
export function nextAll(el, filter, first) {
	let sibs = [];
	while ((el = _el(el).nextElementSibling)) {
		if (
			!filter ||
			(0 === filter.indexOf('.') && hasClass(el, filter.substr(1))) ||
			el.nodeName.toLowerCase() === filter
		) {
			sibs.push(el);
		}
	}
	return first ? sibs[0] : sibs;
}

/**
 * Add styles to an element, similar to jQuery.css()
 *
 * If `style` is a string, and no value is passed, the CSS value of `style` is returned.
 *
 * @param   {Element}       el    The element we want to apply styles to.
 * @param   {Object|string} style An a style, or object of styles, to add to the element.
 * @param   {string}        val   A value to set if only one style is being set.
 *
 * @returns {void|string} The css value as a string.
 */
export function css(el, style, val) {
	if (!_el(el) || !style) {
		throw new Error('Element and style parameters are required.');
	}

	// If this is an object of styles, go ahead and update right away.
	if ('object' === typeof style) {
		let cssText = '';
		for (var property in style) {
			// Convert the property name to spinal case.
			const cssProperty = property.replace(/(?=[A-Z])/g, '-').toLowerCase();

			cssText = cssText + cssProperty + ':' + style[property] + ';';
		}

		// Add the trailing comma to the existing CSS text if it doesn't exist.
		// This is needed to prevent errors from anyone applying styles and
		// missing the trailing `;` on the statement, which is not uncommon.
		if (!el.style.cssText.endsWith(';')) {
			cssText = ';' + cssText;
		}

		// By setting the cssText property, we can apply multiple styles at once,
		// and avoid multiple reflows. This helps to reduce the impact our styles
		// have on the Core Web Vitals INP score of a site.
		el.style.cssText += cssText;

		return;
	}

	// If we don't have a value, it means we want to get the computed style of that css style passed.
	if (!val) {
		return getComputedStyle(el, null).getPropertyValue(style);
	}

	// If css key is set, lets update the style.
	el.style[style] = val;
}

/**
 * Checks if an object or array is empty.
 *
 * @since 5.0.0
 *
 * @param   {Object|Array} obj The object or array to check if it's empty.
 * @returns {boolean}          Whether or not the array or object is empty.
 */
export function empty(obj) {
	try {
		let array = obj;
		if (!Array.isArray(obj)) {
			array = keys(obj);
		}
		return !Array.isArray(array) || !array.length;
	} catch {}

	return !obj;
}

/**
 * Checks if the passed in variable is an object.
 *
 * @since 5.0.0
 *
 * @param   {*}       obj The variable to check if its an object or not.
 * @returns {boolean}     Whether or not the array or object is empty.
 */
export function isObject(obj) {
	return null !== obj && 'object' === typeof obj && !Array.isArray(obj);
}

/**
 * Replace an element on the page.
 *
 * @since 5.0.0
 *
 * @param   {Element} el1 The element you want to replace.
 * @param   {Element} el2 The element you are replacing with.
 * @returns {void}
 */
export function replace(el1, el2) {
	if (_el(el1).parentNode) {
		el1.parentNode.replaceChild(_el(el2), el1);
	}
}

/**
 * Fades in an element on the page.
 *
 * @since 5.0.0
 *
 * @param   {Element} el An element to fade.
 * @param   {Function}   cb A callback function to run once the fade has completed.
 * @returns {void}
 */
export function fadeIn(el, cb) {
	// Ensure the opacity property is set.
	// Only set to 0 if the opacity value is not set or is set to 1.
	_el(el).style.opacity = el.style.opacity && 1 > el.style.opacity ? el.style.opacity : 0;
	el.style.display = 'block';
	el.fade = 'in';

	let f = () => {
		let val = el.style.opacity * 10;
		if ('in' === el.fade && !(10 < (val += 1))) {
			el.style.opacity = val / 10;
			requestAnimationFrame(f);
		} else {
			delete el.fade;
			if (isFunction(cb)) {
				cb();
			}
		}
	};

	// Let's fade!
	f();
}

/**
 * Fades out an element on the page.
 *
 * @since 5.0.0
 *
 * @param   {Element} el An element to fade.
 * @param   {Function}   cb A callback function to run once the fade has completed.
 * @returns {void}
 */
export function fadeOut(el, cb) {
	// Ensure the opacity property is set.
	// Only set to 1 if the opacity value is not already set.
	_el(el).style.opacity = 0 < el.style.opacity ? el.style.opacity : 1;
	el.style.display = 'block';
	el.fade = 'out';

	let f = () => {
		let val = el.style.opacity * 10;
		if ('out' !== el.fade || 0 >= (val -= 1)) {
			delete el.fade;
			el.style.opacity = 0;
			el.style.display = 'none';
			if (isFunction(cb)) {
				cb();
			}
		} else {
			el.style.opacity = val / 10;
			requestAnimationFrame(f);
		}
	};

	// Let's fade!
	f();
}

/**
 * Checks whether an element is visible to the user or not.
 *
 * @since 5.0.0
 *
 * @param   {Element} el The element to check if its visible or not.
 * @returns {boolean}       Whether or not the element is visible.
 */
export function visible(el) {
	return 0 < _el(el).offsetWidth && 0 < el.offsetHeight;
}

/**
 * Scroll from one element to another pixel position on the page.
 *
 * @since 5.0.0
 *
 * @param   {Element}  el The element we are scrolling from/to.
 * @param   {number}   to The pixel position we are scrolling to.
 * @param   {number}   d  How long we want the scroll to last.
 * @param   {Function} cb The callback to call when the scroll is complete.
 * @returns {void}
 */
export function scroll(el, to = 0, d = 1250, cb) {
	let cos = (_el(el).scrollTop - to) / 2;
	let count = 0;
	let oTime = window.performance.now();
	let s = (nTime) => {
		let tsDiff = nTime - oTime;
		// Performance.now() polyfill loads late so passed-in timestamp is a larger offset
		// on the first go-through than we want so I'm adjusting the difference down here.
		// Regardless, we would rather have a slightly slower animation than a big jump so a good
		// safeguard, even if we're not using the polyfill.
		if (100 < tsDiff) {
			tsDiff = 30;
		}
		count += Math.PI / (d / tsDiff);

		// As soon as we cross over Pi, we're about where we need to be
		if (count >= Math.PI) {
			if (to !== el.scrollTop) {
				el.scrollTop = to;
			}
			if (isFunction(cb)) {
				cb();
			}
			return;
		}

		let pos = Math.round(to + cos + cos * Math.cos(count));
		el.scrollTop = pos;
		oTime = nTime;
		requestAnimationFrame(s);
	};

	// Let's scroll!
	requestAnimationFrame(s);
}

/**
 * Checks for a valid email address.
 *
 * Avoids ReDos: https://owasp.org/www-community/attacks/Regular_expression_Denial_of_Service_-_ReDoS
 * Inspired by: https://github.com/sindresorhus/email-regex/blob/9a355500c11834ff7461e60d84c4bdfdca0187e2/index.js#L1
 *
 * @since 5.0.0
 *
 * @param   {string} email Email address to validate.
 * @returns {boolean}      Whether or not the email address is valid.
 */
export function validEmail(email) {
	return new RegExp(/^[^.\s@:](?:[^\s@:]*[^\s@:.])?@[^.\s@]+\.[^.\s@]{2,}(?:\.[^.\s@]{2,})*$/).test(email);
}

/**
 * Checks for a valid phone number.
 *
 * @since 5.18.28
 *
 * @param   {string} phone Phone number to validate.
 * @returns {boolean}      Whether or not the phone number is valid.
 */
export function validPhone(phone) {
	// We will consider valid a phone number that contains >= 5 digits and the allowed characters only: (), -, +, .
	const numberOfDigits = phone.replace(/[^\d]/g, '').toString().length;

	return 5 <= numberOfDigits && !/[^\d\s\-+.()]/.test(phone);
}

/**
 * Recursively replaces using provided pattern until no longer matched.
 *
 * @since  5.3.4
 *
 * @param   {string} input   The value to do replacements.
 * @param   {Regex}  pattern The regex match/replace pattern.
 * @returns {string}         The modified value.
 */
export function recursiveReplace(input, pattern) {
	recursiveReplace.count = 0;
	input = input || 0 === input ? String(input).trim().toLowerCase() : '';

	while (input.length && input.match(pattern)) {
		recursiveReplace.count++;
		input = input.replace(pattern, '');
	}

	return input;
}

/**
 * Takes a path value, and cleans it up, including:
 * - Casting to string
 * - Trimming
 * - Removing beginning slashes
 * - Converting to lowercase
 *
 * @since 5.3.4
 *
 * @param  {string} path The (url) path value.
 *
 * @returns {string}      The cleaned up path value.
 */
export function cleanPathWithTrailing(path) {
	return recursiveReplace(path, /^\//g);
}

/**
 * Takes a path value, and cleans it up, including:
 * - Casting to string
 * - Trimming
 * - Removing beginning slashes
 * - Converting to lowercase
 *
 * @since 5.3.4
 *
 * @param   {string} path The (url) path value.
 * @returns {string}      The cleaned up path value.
 */
export function cleanPathWithPreceding(path) {
	return recursiveReplace(path, /\/$/g);
}

/**
 * Takes a path value, and cleans it up, including:
 * - Casting to string
 * - Removing beginning/ending slashes
 * - Converting to lowercase
 * - Trimmed
 *
 * @since 5.0.27
 *
 * @param   {string} path The (url) path value.
 * @returns {string}      The cleaned up path value.
 */
export function cleanPath(path) {
	return cleanPathWithPreceding(cleanPathWithTrailing(path));
}

/**
 * Takes a path value, and cleans it up, including:
 * - Casting to string
 * - Removing beginning/ending slashes
 * - Converting to lowercase
 * - Trimmed
 *
 * @since 5.22.52
 *
 * @param   {string} path     The (url) path value.
 * @param   {string} operator The rule operator value.
 * @returns {string}          The cleaned up path value.
 */
export function cleanOperatorPath(path, operator) {
	let cleanedPath = '';

	// Check operator, update url string appropriately
	switch (operator) {
		case 'contains':
		case 'notContains':
			cleanedPath = String(path).trim().toLowerCase();
			break;

		case 'startsWith':
		case 'notStartsWith':
			cleanedPath = cleanPathWithTrailing(path);
			break;

		case 'endsWith':
		case 'notEndsWith':
			cleanedPath = cleanPathWithPreceding(path);
			break;

		case 'urlOnHomepage':
			// do nothing, cleanedPath is already an empty string, and we want '' for homepage
			break;

		default:
			cleanedPath = cleanPath(path);
			break;
	}

	return cleanedPath;
}

/**
 * Removes an element from the DOM.
 *
 * @since 5.0.0
 *
 * @param   {Element} el The element to remove.
 * @returns {void}
 */
export function remove(el) {
	_el(el).parentNode.removeChild(el);
}

/**
 * Validates a DOM element to make sure it is actually an element.
 *
 * @since 5.0.0
 *
 * @param   {Element} el The dom element to validate.
 * @returns {Element}    Returns the dom element.
 */
export function _el(el) {
	if (
		!(el instanceof Element) &&
		!(el instanceof HTMLElement) &&
		!(el instanceof SVGElement) &&
		!(el === window) &&
		!(el === document)
	) {
		throw new Error('You must pass in a valid DOM element.');
	}

	return el;
}

/**
 * This is a wrapper for the DOM method querySelectorAll
 * which converts the returned NodeList into an array.
 * This is necessary because Safari 9 doesn't always like to
 * iterate properly through NodeLists. Check out
 * https://github.com/awesomemotive/optinmonster-api-js/pull/68
 * for more details.
 *
 * @since 5.0.49
 *
 * @param   {string}       s The selector to to pass to querySelectorAll.
 * @param   {Element|null} c The contextual element to use for querying.
 * @returns {Array}          The array converted from the NodeList.
 */
export function querySelectorAll(s, c) {
	let e = _el(c ? c : document);

	return [].slice.call(e.querySelectorAll(s));
}

/**
 * Checks whether or not `localStorage`/`sessionStorage` is available.
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#Testing_for_availability}
 * @see {@link https://gist.github.com/paulirish/5558557}
 *
 * @since 5.0.0
 *
 * @param   {string} type The storage type. Either `localStorage` or `sessionStorage`.
 * @returns {boolean}     Is `localStorage`/`sessionStorage` available or not?
 */
export function storageAvailable(type) {
	try {
		let storage = window[type];
		let x = '__storage_test__';
		storage.setItem(x, x);
		storage.removeItem(x);
		return true;
	} catch (e) {
		return (
			e instanceof DOMException &&
			// everything except Firefox
			(22 === e.code ||
				// Firefox
				1014 === e.code ||
				// test name field too, because code might not be present
				// everything except Firefox
				'QuotaExceededError' === e.name ||
				// Firefox
				'NS_ERROR_DOM_QUOTA_REACHED' === e.name) &&
			// acknowledge QuotaExceededError only if there's something already stored
			0 === window[type].length
		);
	}
}

/**
 * Gets the true outer height of an element
 * including all children with collapsing margins.
 *
 * @since 5.9.15
 *
 * @param   {Element} e The element to check the height of.
 * @param   {string}  p The position of the floating element.
 * @param   {Array}   s An array of elements to skip over.
 * @returns {number}    The height of the element as an integer.
 */
export function floatingHeight(e, p = 'top', s = []) {
	let all = [];
	let height = (el) => {
		let b = el.getBoundingClientRect();

		// If the element has no width or height, return early.
		if (0 === b.height) {
			return;
		}

		// Do the math.
		let height =
			'top' === p
				? b.height + (0 > b.top ? 0 : b.top)
				: Math.max(document.documentElement.clientHeight, window.innerHeight || 0) - b.top;

		// Do the math and add it to the all array.
		all.push(Math.round(height));
	};
	let loop = (els) => {
		each(els, (i, el) => {
			// If we should skip this element, return early.
			if (inArray(el, s)) {
				return;
			}

			if (el & el.children && el.children.length) {
				loop(el.children);
			}
			height(el);
		});
	};

	// Set our element in an array to begin the recursive loop.
	loop([e]);

	// if the parent element has a margin on top, let's subtract that.
	let m = 0;
	if (e.style.marginTop && 'top' === p) {
		m -= parseInt(e.style.marginTop);
	}

	// Return the highest number or 0.
	return all.length ? Math.abs(Math.max(...all)) + m : 0;
}

/**
 * Store's folders for the current campaign.
 *
 * @since 5.11.31
 *
 * @param   {Array}  f An array of folders to loop through.
 * @param   {string} k A string key to look up in local storage.
 * @param   {string} v A string value to add to local storage.
 * @returns {void}
 */
export function storeFolders(f, k, v) {
	if (storageAvailable('localStorage')) {
		let fs = JSON.parse(window.localStorage.getItem(k)) || {};
		each(f, (i, fo) => {
			if (!fs[fo.id]) {
				fs[fo.id] = [];
			}

			if (-1 === fs[fo.id].indexOf(v)) {
				fs[fo.id].push(v);
			}
		});

		window.localStorage.setItem(k, JSON.stringify(fs));
	}
}

/**
 * Retrieves revenue attribution data.
 *
 * @returns {Object} All revenue attribution data.
 */
export function getRevenue() {
	return JSON.parse(decodeURIComponent(Cookie.get('_omra'))) || {};
}

/**
 * Stores revenue attribution event data for the current campaign.
 *
 * @param   {string} c The campaign ID.
 * @param   {string} v A string value to add to local storage.
 * @returns {void}
 */
export function storeRevenueAttributionEvent(c, v) {
	let ra = getRevenue();
	let ac = ra?.[c];

	// "click" attribution always takes precedence.
	// Don't allow "view" to override it.
	if (!ac || 'view' === ac) {
		ra[c] = v;
	}

	// Make revenue attribution data stick for one year, and make it work cross domain by default.
	Cookie.create('_omra', encodeURIComponent(JSON.stringify(ra)), Cookie.getExpiration(365), true);
}

/**
 * Decodes HTML Entities
 * This is done by adding them to a fake textarea,
 * then returning the value.
 *
 * @since 5.13.62
 *
 * @param {string} text Text to escape
 * @returns {string} The escaped string
 */
export function decodeHtmlEntities(text) {
	let textArea = document.createElement('textarea');
	textArea.innerHTML = text;
	return textArea.value;
}

/**
 * Converts a URL (string) into a parsed object.
 *
 * @since 5.16.1
 *
 * @param   {string} u URL to parse.
 * @returns {Object}   The parsed URL.
 */
export function parseUrl(u) {
	let p = document.createElement('a');
	let o = {};
	let q;
	let s;
	let i;

	// Let the browser do the work.
	p.href = u;

	// Convert query string to object.
	q = p.search.replace(/^\?/, '').split('&');
	for (i = 0; i < q.length; i++) {
		s = q[i].split('=');
		o[s[0]] = s[1];
	}

	return {
		protocol: p.protocol,
		host: p.host,
		hostname: p.hostname,
		port: p.port,
		pathname: p.pathname,
		search: p.search,
		query: o,
		hash: p.hash,
	};
}

/**
 * Returns a parsed URL based on parameters.
 *
 * @since 5.16.1
 *
 * @param   {Object} d The data/defaults object.
 * @param   {string} u URL key to lookup with.
 * @param   {string} p Path to append.
 * @returns {Object}   The parsed URL.
 */
export function getUrl(d, u, p = '') {
	let url = g.URLS[u];
	let envMin = d.envMin;
	let envApi = d.wp ? 'api.' : d.envApi + '.';
	let aDomain = omApiDomain(d);
	let cDomain = 'a.' + aDomain;
	let zDomain = 'z.' + aDomain;
	let mDomain = g.MC_DOMAIN;

	if ('dev' === d.env) {
		// If we're in the dev environment, and requesting the ApiDebug.js or the lsi.min.js,
		// request the local copy, like we do for Api.js.
		switch (u) {
			case 'css':
			case 'debug':
			case 'lsi':
			case 'soundEffects':
				url = g.URLS[u + 'Dev'];
				break;
		}

		if (!inArray(u, ['geolocation', 'soundEffects', 'adblock', 'fonts', 'fontAwesome'])) {
			envMin = '';
			envApi = '';
			cDomain = g.DEV_DOMAIN;
			aDomain = g.DEV_DOMAIN;
			mDomain = g.DEV_DOMAIN;
		}
	} else if (d.cname) {
		envMin = d.envMin;
		envApi = '';
		cDomain = cdnDomain(d.cname, '/a');
		aDomain = apiDomain(d.cname, '/api');
		zDomain = apiDomain(d.cname, '/z');
	}

	url = url
		.replace('{env-min}', envMin)
		.replace('{env-api}', envApi)
		.replace('{cDomain}', replaceAssetDomains(cDomain))
		.replace('{aDomain}', aDomain)
		.replace('{zDomain}', zDomain)
		.replace('{mDomain}', mDomain);

	return `${url}${p}`;
}

/**
 * Returns the proper CDN domain based on whether we are using the CDN or not.
 *
 * @since 5.16.1
 *
 * @param   {Object} c The CNAME object.
 * @param   {string} p The path to append to the domain.
 * @returns {string}   The domain to use.
 */
export function cdnDomain(c, p) {
	return c.cdn ? c.domain.host : c.domain.host + p;
}

/**
 * Returns the proper API domain based on whether we are using the CDN or not.
 *
 * @since 5.16.1
 *
 * @param   {Object} c The CNAME object.
 * @param   {string} p The path to append to the domain.
 * @returns {string}   The domain to use.
 */
export function apiDomain(c, p) {
	if (!c.cdn) {
		return c.domain + p;
	}

	// If the CDN is default, we need to compile the API url.
	let url = c.domain.host.split('.');

	// Replace the first subdomain with the api one.
	url[0] = c.api;

	return url.join('.') + p;
}

/**
 * Replaces old asset domains in a string with the latest asset domain.
 *
 * @since 5.20.86
 *
 * @param   {string} s The string to do the search & replace.
 * @returns {string}   The modified string.
 */
export function replaceAssetDomains(s) {
	// Bail early if empty/no domain is passed.
	if (!s) {
		return s;
	}

	// If no domains are present, return the unmodified string.
	if (!window[g.GLOBAL_OM]?.domains?.replace) {
		return s;
	}

	each(window[g.GLOBAL_OM].domains.replace, (i, domain) => {
		s = s.replace(new RegExp(domain, 'g'), window[g.GLOBAL_OM].domains.replacement);
	});

	return s;
}

/**
 * String startsWith helper method.
 *
 * Used to avoid conflict with other possible versions of the same method. This
 * is roughly the same code as used for the `Polyfills.startsWith` polyfill, just
 * slightly modified to work outside of the polyfill context. Any
 * changes to this, should likely be applied to `Polyfills.startsWith` as well.
 * {@link https://github.com/awesomemotive/optinmonster-api-js/issues/445}
 *
 * @since 5.18.39
 *
 * @param   {string} l The string where to search for the characters.
 * @param   {string} s The characters to be searched for at the end of this string.
 * @param   {number} r The position in this string at which to begin searching for searchString; defaults to 0.
 * @returns {bool}     True if the given characters are found at the beginning of the string.
 */
export function startsWith(l, s, r) {
	let p = 0 < r ? r | 0 : 0;
	return l.substring(p, p + s.length) === s;
}

/**
 * A safe and ESLint-compatible way to check if an object contains a property.
 *
 * {@link https://eslint.org/docs/rules/no-prototype-builtins}
 * {@link https://eslint.org/docs/rules/guard-for-in}
 * {@link https://github.com/eslint/eslint/issues/7071}
 *
 * @since 5.19.65
 *
 * @param {Object} obj  The object to check.
 * @param {string} prop The to check.
 *
 * @returns {boolean} True if the property exists.
 */
export function hasProp(obj, prop) {
	return Object.prototype.hasOwnProperty.call(obj, prop);
}

/**
 * Returns the requested query string.
 *
 * @since 5.19.65
 *
 * @param {string} arg     The query arg to check.
 * @param {Object} argsObj Query args object. Expects return of `queryArgs()`.
 *
 * @returns {string|null} The query string value. Defaults to empty string.
 */
export function getQueryArg(arg, argsObj) {
	const args = argsObj || queryArgs();
	return args && hasProp(args, arg) ? args[arg] : null;
}

/**
 * Determines if given query string exists.
 *
 * @since 5.19.65
 *
 * @param {string} arg     The query arg to check.
 * @param {Object} argsObj Query args object. Expects return of `queryArgs()`.
 *
 * @returns {boolean} The value if found.
 */
export function hasQueryArg(arg, argsObj) {
	return null !== getQueryArg(arg, argsObj);
}

/**
 * Determines if given query string is truthy.
 *
 * NOTE: This helper requires a query argument to have an explicit falsy value.
 *       If a query argument is appended to a URL with no value, `getQueryArg`
 *       returns an empty string. While this technically evaluates to falsy,
 *       it's not an explicit falsy value, and, as such, won't cause this
 *       function to return `false`.
 *
 * @since 5.19.65
 *
 * @param {string} arg     The query arg to check.
 * @param {Object} argsObj Query args object. Expects return of `queryArgs()`.
 *
 * @returns {boolean} Is it truthy?
 */
export function queryArgTruthy(arg, argsObj) {
	const val = getQueryArg(arg, argsObj);
	return null !== val && 'false' !== val && '0' !== val;
}

/**
 * Get a header from an XHR object.
 * By checking if the header exists first, will prevent Chromium from firing off
 * the "non-error" "Refused to get unsafe header" messages in the console,
 * when the header does not exist.
 *
 * @see https://trackjs.com/blog/refused-unsafe-header/
 *
 * @since  5.20.65
 *
 * @param  {XMLHttpRequest} xhr    The xhr object to get the header from.
 * @param  {string}         header The header key to fetch.
 *
 * @returns {string} The requested header value, or empty string.
 */
export function getHeader(xhr, header) {
	const all = xhr.getAllResponseHeaders();
	return all && inArray(header.toLowerCase(), all.toLowerCase()) ? xhr.getResponseHeader(header) : '';
}

/**
 * Try to get a DOM element with the querySelector.
 * Will return null if the querySelector throws an error, or if it does not exist
 *
 * @since 5.20.83
 *
 * @param {string} querySelector The query selector to search for
 *
 * @returns {Object} Attempts to returns the HTML Element object, or null
 */
export function getDOMElement(querySelector) {
	try {
		return document.querySelector(querySelector);
	} catch {
		return null;
	}
}

/**
 * Try to get the browser user agent.
 * Will return an empty string if the an error is thrown or if it does not exist.
 *
 * @since 5.22.48
 *
 * @returns {string} Returns the user agent or an empty string
 */
export function getUserAgent() {
	try {
		return navigator.userAgent || '';
	} catch {
		return '';
	}
}

/**
 * Determine if the given flag exists/is truthy.
 *
 * Note: all flag names should begin with "om".
 *
 * @since 5.22.66
 *
 * @param {string} name The arg/cookie name
 *
 * @returns {boolean} Is the query arg truthy or is there a cookie?
 */
export function hasTestFlag(name) {
	return !!getTestFlag(name);
}

/**
 * Get the value of a test flag.
 *
 * Note: all flag names should begin with "om".
 *
 * @since 5.22.70
 *
 * @param {string} name The query arg name.
 * @param {string} cookieName The cookie name (falls back to the name arg).
 *
 * @returns {boolean} The value of the test flag query arg or cookie.
 */
export function getTestFlag(name, cookieName) {
	return getQueryArg(name) || Cookie.get(cookieName || name) || '';
}

/**
 * Get the translated text for a string. If not available, returns the string directly.
 *
 * @since 5.25.38
 *
 * @param {string} text    The text to translate.
 * @param {string} replace Any strings you want to replace
 *
 * @returns {string} The text translated (or not)
 */
export function __(text, ...replace) {
	// Replace any arguments in the string, in order.
	let translated = replace.reduce((acc, arg) => acc.replace('%s', arg), text);

	if (!g.STRINGS[translated]) {
		g.STRINGS[translated] = translated;
	}

	return g.STRINGS[translated];
}

/**
 * Returns a string with leading zeros added to meet the target length.
 *
 * This is a port of the `addLeadingZeros` function from `date-fns` package.
 *
 * @see {@link https://github.com/date-fns/date-fns/blob/871ea7a3476f7af03d100e2b48c1b49e98acc738/src/_lib/addLeadingZeros/index.ts}
 *
 * @param {number} number       The number to (maybe) receive leading zeros.
 * @param {number} targetLength The target string length.
 *
 * @returns {string} The padded string.
 */
export function addLeadingZeros(number, targetLength) {
	const sign = 0 > number ? '-' : '';
	let output = Math.abs(number).toString();

	while (output.length < targetLength) {
		output = '0' + output;
	}

	return sign + output;
}

/**
 * This allows us to trigger a custom event.
 *
 * @since 5.0.0
 *
 * @param   {Element} el    The element we are adding an event to.
 * @param   {string}  event The "event" that we are going to listen to, this will be prefixed with "om.".
 * @param   {Object}  data  An object of data that will be available in the event.detail property.
 * @returns {void}
 */
export function trigger(el, event, data = {}) {
	let c = event[0] !== event[0].toLowerCase();
	let ev = null;

	if (c) {
		ev = new CustomEvent('om.' + event, {
			bubbles: true,
			cancelable: true,
			detail: data,
		});
	} else {
		ev = new Event(event, {
			bubbles: true,
			cancelable: true,
		});
	}

	// Dispatch the event.
	el.dispatchEvent(ev);
}

/**
 * Determines if the visitor is returning to the site.
 *
 * @returns {boolean} Returns the comparison value.
 */
export function visitorReturning() {
	return null !== Cookie.getPersistent() && null === Cookie.getSession();
}

/**
 * Checks if an element is out of the visible viewport.
 * This considers the element's position relative to the viewport,
 * determining if it's fully or partially out of view.
 *
 * @param {Element} element The DOM element to check.
 * @returns {boolean} Returns `true` if the element is out of the viewport, otherwise `false`.
 */
export function isElementOutOfView(element) {
	// If the element is not an HTMLElement, return early.
	if (!element || !(element instanceof HTMLElement)) {
		return true;
	}

	const rect = element.getBoundingClientRect();
	const viewportHeight = window.innerHeight;
	const viewportWidth = window.innerWidth;

	// Check if the element is completely out of the viewport
	const isOutOfView =
		rect.top > viewportHeight || // Element is below the viewport
		0 > rect.bottom || // Element is above the viewport
		rect.left > viewportWidth || // Element is to the right of the viewport
		0 > rect.right; // Element is to the left of the viewport

	return isOutOfView;
}

/**
 * Focus on an element if it's inside the view.
 *
 * @param {Element} element The DOM element to focus.
 * @returns {void}
 */
export function focusIfInView(element) {
	if (!isElementOutOfView(element)) {
		element.focus();
	}
}

/**
 * Get the GA Block ID of a block if there is any.
 *
 * @param {Element} element The element is being tracked for conversion.
 *
 * @returns {string|null} Returns the block ID if exists, null otherwise.
 */
export function getGaBlockId(element) {
	// Find closest ancestor (including self) with data-gablockid attribute
	const gaElement = element.closest('[data-gablockid]');
	// Return the gablockid value if found, null otherwise
	return gaElement?.dataset.gablockid ?? null;
}

/**
 * Get a value from an object using a path.
 *
 * Adapted from https://youmightnotneed.com/lodash/#get
 *
 * @param {Object} obj     The object to get the value from.
 * @param {string} path    The path to the value.
 * @param {*} defaultValue The default value to return if the path is not found.
 *
 * @returns {*} The value from the object.
 */
export function getJsVariable(obj, path, defaultValue) {
	// If path is falsy or has multiple consecutive dots, return undefined.
	if (!path || path.match(/[.]{2,}/g)) {
		return undefined;
	}

	// Check if path is string or array. Regex : ensure that we do not have '.' and brackets.
	// Regex explained: https://regexr.com/58j0k
	const pathArray = path.match(/([^[.\]])+/g);

	// Navigate through the each path item to find the value.
	const result = pathArray.reduce((prevObj, key) => prevObj && prevObj[key], obj);

	return result === undefined ? defaultValue : result;
}

/**
 * Escapes a string for use in HTML.
 *
 * See https://youmightnotneed.com/lodash/#escape
 *
 * @param {string} string The string to escape.
 *
 * @returns {string} The escaped string.
 */
export function escape(string) {
	const map = {
		'&': '&amp;',
		'<': '&lt;',
		'>': '&gt;',
		'"': '&quot;',
		"'": '&#39;',
	};

	return string.replace(/[&<>"']/g, (m) => map[m]);
}
